import React from 'react';
import { Panel, PanelType } from '@fluentui/react';
import { IStorefrontSettings } from '../../../../models/Storefront';
import { ISearchElementStyle } from '../searchPageConfig';
import { IHeaderConfig, parseHeaderConfig } from '../headerFooterConfig';
import { ElementPropertyRows, StyleOverridesGroup, StylePartAccordions } from '../styleControls';
import { styleTargetForKey } from '../stylesConfig';
import { resolvedStyleValue } from '../styleConfig';
import AccountPersonaExample from './AccountPersonaExample';

// Styling for the header's account control — the persona a shopper clicks to sign in, and the same
// control once they're signed in.
//
// It gets its own flyout rather than rows on the header preview for the reason the suggestions panel
// does: five style parts with the full control set would swamp a header mock-up. And it is ONE panel
// covering both states on purpose — signed out the control reads "Guest / Sign in", signed in it
// shows the shopper's name, but it is the same control in the same slot, and letting the two be
// styled apart is how a header ends up looking like it has two different buttons.

type AccountPart = 'account' | 'accountName' | 'accountSubtext' | 'accountAvatar' | 'accountPanel';

const PARTS: AccountPart[] = ['account', 'accountName', 'accountSubtext', 'accountAvatar', 'accountPanel'];

const PART_LABELS: Record<AccountPart, string> = {
    account: 'Button',
    accountName: 'Name',
    accountSubtext: 'Sign in text',
    accountAvatar: 'Avatar',
    accountPanel: 'Sign-in popover',
};

const PART_TOOLTIPS: Record<AccountPart, string> = {
    account: 'The clickable pill in the header. Defaults to a light grey rounded pill.',
    accountName: 'The top line: the shopper\'s name, or "Guest" when signed out.',
    accountSubtext: 'The second line, "Sign in". Only shown when signed out.',
    accountAvatar: 'The round avatar beside the name.',
    accountPanel: 'The popover that drops down with the sign-in form.',
};

interface IStorefrontAccountPersonaStylePanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    storefrontSettings: IStorefrontSettings;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
}

const StorefrontAccountPersonaStylePanel: React.FC<IStorefrontAccountPersonaStylePanelProps> = (props) => {
    const headerConfig: IHeaderConfig = React.useMemo(
        () => parseHeaderConfig(props.storefrontSettings.headerConfig),
        [props.storefrontSettings.headerConfig]);

    const updatePart = (part: AccountPart, patch: Partial<ISearchElementStyle>) => {
        // Re-read from the live settings rather than the memo above, so quick edits across parts
        // can't clobber one another.
        const current = parseHeaderConfig(props.storefrontSettings.headerConfig);
        props.onStorefrontSettingsChange({
            ...props.storefrontSettings,
            headerConfig: JSON.stringify({ ...current, [part]: { ...current[part], ...patch } }),
        });
    };

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText='Account & Sign In'
            type={PanelType.smallFixedFar}
            isBlocking={false}
            closeButtonAriaLabel='Close'
        >
            <p style={{ margin: '0 0 0.5em', fontSize: 12, color: '#605e5c' }}>
                The account control in the header. These styles apply both when a shopper is signed out
                (where it reads &quot;Guest / Sign in&quot;) and once they are signed in.
            </p>

            {/* Sits above the parts so an edit is visible without leaving the panel. */}
            <div style={{ marginBottom: '1em' }}>
                <AccountPersonaExample
                    trigger={headerConfig.account}
                    name={headerConfig.accountName}
                    subtext={headerConfig.accountSubtext}
                    avatar={headerConfig.accountAvatar}
                    headerColor={resolvedStyleValue(props.storefrontSettings, 'menuBarColor')}
                    headerTextColor={resolvedStyleValue(props.storefrontSettings, 'menuBarTextColor')}
                />
            </div>
            <StylePartAccordions
                parts={PARTS.map((part) => ({
                    key: part,
                    label: PART_LABELS[part],
                    tooltip: PART_TOOLTIPS[part],
                    content: (
                        <StyleOverridesGroup
                            rowLayout
                            target={styleTargetForKey(part)}
                            value={headerConfig[part]}
                            onChange={(patch) => updatePart(part, patch)}
                        >
                            <ElementPropertyRows settingKeyPrefix={`storefront.accountPersona.${part}`}
                                value={headerConfig[part]}
                                onChange={(patch) => updatePart(part, patch)}
                            />
                        </StyleOverridesGroup>
                    ),
                }))}
            />
        </Panel>
    );
};

export default StorefrontAccountPersonaStylePanel;
